import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import useIsMobile from '../utils/useIsMobile';

// --- DATA ---
// --- DATA ---
const DEFAULT_HERO_IMAGE = 'https://skyphaseobeng.s3.eu-north-1.amazonaws.com/casual2.JPG';

const DEFAULT_COLLECTIONS = [
  {
    id: 'nuptials',
    title: 'BRIDALS',
    subtitle: 'ELEGANCE & GRACE',
    img: 'https://skyphaseobeng.s3.eu-north-1.amazonaws.com/bridal4.jpg',
    type: 'portrait',
    path_name: '/bookings',
  },
  {
    id: 'exclusives',
    title: 'EXCLUSIVES',
    subtitle: 'ONE OF A KIND',
    img: 'https://skyphaseobeng.s3.eu-north-1.amazonaws.com/1emba.JPG',
    type: 'portrait',
    path_name: '/bookings',
  },
  {
    id: 'kente',
    title: 'KENTE',
    subtitle: 'TRADITION MEETS MODERN',
    img: 'https://skyphaseobeng.s3.eu-north-1.amazonaws.com/kente2.JPG',
    type: 'portrait',
    path_name: '/bookings',
  },
  {
    id: 'casuals',
    title: 'CASUALS',
    subtitle: 'EVERYDAY LUXURY',
    img: 'https://skyphaseobeng.s3.eu-north-1.amazonaws.com/casual3.JPG',
    type: 'portrait',
    path_name: '/bookings',
  },
  {
    id: 'bridal-2',
    title: 'THE WEDDING EDIT',
    subtitle: 'FOR THE SPECIAL DAY',
    img: 'https://skyphaseobeng.s3.eu-north-1.amazonaws.com/bridal1.JPG',
    type: 'landscape',
    path_name: '/bookings',
  },
];

// --- COMPONENTS ---

function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);

    // Load user
    const userData = localStorage.getItem('emba_user');
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        setUser(parsed.user || parsed);
      } catch (e) {
        console.error("Failed to parse user", e);
      }
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const displayName = user ? `${user.first_name} ${user.last_name}` : 'ACCOUNT';

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-6'
          }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="w-full px-6 md:px-12 flex items-center justify-between">
          {/* Mobile Menu Button (Left on mobile, hidden on desktop) */}
          <button
            className={`md:hidden p-2 ${isScrolled ? 'text-gray-900' : 'text-white'}`}
            onClick={() => setMobileOpen(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>

          {/* Left Nav (Desktop) */}
          <nav className={`hidden md:flex flex-1 gap-10 items-center text-[10px] tracking-widest font-medium ${isScrolled ? 'text-gray-800' : 'text-white'}`}>
            <Link to="/" className="hover:opacity-60 transition-opacity">HOME</Link>
            <Link to="/bookings" className="hover:opacity-60 transition-opacity">BOOKINGS</Link>
            <Link to="/store" className="hover:opacity-60 transition-opacity">SHOP</Link>
          </nav>

          {/* Center Logo */}
          <div className="flex-1 flex justify-center items-center gap-3">
            <img src="/logo.png" alt="Logo" className="h-6 w-6 object-contain" />
            <Link to="/" className={`text-sm tracking-[0.3em] font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
              EMBA BY EMEFA
            </Link>
          </div>

          {/* Right Nav (Desktop) */}
          <nav className={`hidden md:flex flex-1 justify-end gap-10 items-center text-[10px] tracking-widest font-medium ${isScrolled ? 'text-gray-800' : 'text-white'}`}>
            {user && user.role === 'ADMIN' && (
              <Link to="/all-appointments" className="hover:opacity-60 transition-opacity">ALL APPOINTMENTS</Link>
            )}
            {localStorage.getItem('emba_access') ? (
              <Link to="/profile" className="hover:opacity-60 transition-opacity uppercase">{displayName}</Link>
            ) : (
              <Link to="/login" className="hover:opacity-60 transition-opacity">SIGN IN</Link>
            )}
          </nav>
          
          {/* Empty div for mobile spacing balance */}
          <div className="w-10 md:hidden"></div>
        </div>
      </motion.header>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] flex justify-end">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            className="relative w-64 bg-white h-full shadow-2xl flex flex-col"
          >
            <div className="p-6 flex justify-between items-center border-b">
              <span className="text-xs tracking-[0.2em] font-bold">MENU</span>
              <button onClick={() => setMobileOpen(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="flex flex-col p-6 gap-6 text-sm tracking-widest">
              <Link to="/store" className="bg-black text-white px-4 py-3 font-bold text-center hover:bg-gray-800 transition-colors">SHOP</Link>
              <Link to="/" className="hover:text-gray-500">HOME</Link>
              <Link to="/bookings" className="hover:text-gray-500">BOOKINGS</Link>
              {user && user.role === 'ADMIN' && (
                <Link to="/all-appointments" className="hover:text-gray-500">ALL APPOINTMENTS</Link>
              )}
              {localStorage.getItem('emba_access') ? (
                <Link to="/profile" className="hover:text-gray-500 uppercase">{displayName}</Link>
              ) : (
                <Link to="/login" className="hover:text-gray-500">SIGN IN</Link>
              )}
            </nav>
          </motion.aside>
        </div>
      )}
    </>
  );
}

function Hero({ image, banners, currentBannerIndex, isLoading }) {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 1000], [0, 400]);
  const isMobile = useIsMobile();
  
  const banner = banners && banners.length > 0 ? banners[currentBannerIndex] : null;
  
  let displayImage = image || (!isLoading ? DEFAULT_HERO_IMAGE : null);
  if (banner) {
    if (isMobile && banner.mobile_image_url) {
      displayImage = banner.mobile_image_url;
    } else if (banner.image_url) {
      displayImage = banner.image_url;
    }
  }

  return (
    <section className="relative h-screen w-full overflow-hidden bg-[#1b0d0b]">


      <motion.div style={{ y }} className="absolute inset-0">
        <AnimatePresence mode="wait">
          {displayImage && (
            <motion.img
              key={displayImage}
              initial={{ opacity: 0, scale: 1.1, filter: 'brightness(0.5)' }}
              animate={{ opacity: 1, scale: 1, filter: 'brightness(0.8)' }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
              src={displayImage}
              alt="Hero"
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
          )}
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1b0d0b]/80 via-transparent to-black/30" />
      </motion.div>

      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center text-[#EBE6DD] px-4 pointer-events-none">

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 1.0, duration: 1 }}
          className="absolute bottom-12 flex flex-col items-center gap-4"
        >
          <span className="text-[9px] tracking-[0.3em] text-[#EBE6DD]/60 uppercase">Scroll to explore</span>
          <div className="w-[1px] h-12 bg-[#EBE6DD]/30 relative overflow-hidden">
            <motion.div 
              className="w-full h-full bg-[#EBE6DD] absolute top-0 left-0"
              animate={{ y: ['-100%', '100%'] }}
              transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
            />
          </div>
        </motion.div>
      </div>

      {/* Sale Promotion Block */}
      <AnimatePresence mode="wait">
        {banner && (
          <motion.div 
            key={banner.id || displayImage}
            className="absolute bottom-8 md:bottom-16 left-6 md:left-12 z-20 flex flex-col items-start text-white pointer-events-auto"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.8 }}
          >
            <Link 
              to={banner.path_name || "/store"}
              className="bg-[#ff0000] text-white text-[9px] md:text-[10px] font-bold px-4 py-1.5 rounded-full mb-6 uppercase tracking-[0.2em] inline-block hover:bg-red-700 transition-colors"
            >
              {banner.type || 'STORE'}
            </Link>
            {banner.header && (
              <h3 className="text-2xl md:text-3xl font-light mb-3">{banner.header}</h3>
            )}
            {banner.title && (
              <p className="text-xs md:text-sm max-w-xs mb-6 leading-relaxed opacity-80">
                {banner.title}
              </p>
            )}
            {(banner.price || banner.was_price) && (
              <div className="flex flex-col mb-2">
                {banner.price && <span className="text-[#ff0000] font-bold text-2xl md:text-3xl tracking-tight">₵ {banner.price}</span>}
                {banner.was_price && <span className="line-through text-white/60 text-sm mt-1">₵ {banner.was_price}</span>}
              </div>
            )}
            {banner.subtitle && (
              <span className="text-[#ff0000] text-[10px] font-bold uppercase tracking-widest mt-1">
                {banner.subtitle}
              </span>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function KineticTypography() {
  const { scrollYProgress } = useScroll();
  
  // Smooth the scroll progress so the text doesn't jitter
  const smoothProgress = useSpring(scrollYProgress, { damping: 50, stiffness: 400 });
  
  // Move horizontally based on scroll
  const x = useTransform(smoothProgress, [0, 1], [0, -1500]);
  const x2 = useTransform(smoothProgress, [0, 1], [-1500, 0]);

  return (
    <section className="py-24 md:py-32 bg-[#EBE6DD] overflow-hidden flex flex-col gap-4 border-y border-[#13110E]/10">
      <motion.div style={{ x }} className="flex whitespace-nowrap">
        {[...Array(4)].map((_, i) => (
          <span key={i} className="text-6xl md:text-9xl font-serif text-[#13110E] tracking-tighter shrink-0 px-8">
            EMBA BY EMEFA &mdash; CURATED EXCELLENCE &mdash;
          </span>
        ))}
      </motion.div>
      <motion.div style={{ x: x2 }} className="flex whitespace-nowrap">
        {[...Array(4)].map((_, i) => (
          <span key={i} className="text-6xl md:text-9xl font-serif text-[#13110E] tracking-tighter shrink-0 px-8 opacity-20">
            DISCOVER YOUR STYLE &mdash; LUXURY REDEFINED &mdash;
          </span>
        ))}
      </motion.div>
    </section>
  );
}

function GalleryItem({ item }) {
  const navigate = useNavigate();
  const isLandscape = item.type === 'landscape';
  const spanClass = isLandscape ? 'md:col-span-2' : 'md:col-span-1';
  const aspectClass = isLandscape ? 'aspect-[4/3]' : 'aspect-[2/3]';

  return (
    <motion.div
      onClick={() => navigate(item.path_name || "/bookings")}
      className={`relative group overflow-hidden rounded-sm ${spanClass} ${aspectClass} bg-neutral-100 cursor-pointer`}
      initial={{ opacity: 0, y: 100 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
    >
      <img
        src={item.img}
        alt={item.title}
        className="h-full w-full object-cover object-top transition-transform duration-[1.5s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors duration-700" />

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 p-4">
        <span className="text-[10px] tracking-[0.3em] text-white/80 mb-2 uppercase">{item.subtitle}</span>
        <h3 className="text-2xl md:text-3xl text-white font-serif tracking-widest mb-6">{item.title}</h3>
        <span className="px-6 py-2 border border-white/50 text-white text-[10px] tracking-[0.2em] hover:bg-white hover:text-black transition-colors">
          EXPLORE
        </span>
      </div>
    </motion.div>
  );
}

function Footer() {
  return (
    <footer className="bg-[#1b0d0b] text-white/80 py-16 border-t border-white/10">
      <div className="mx-auto max-w-6xl px-6">
        <div className="flex flex-col items-center justify-center gap-8">
          <div className="text-xs tracking-[0.4em] text-white/60">
            EMBA BY EMEFA
          </div>

          <div className="flex flex-col md:flex-row gap-8 md:gap-16 text-center">
            <div>
              <h4 className="text-[10px] font-bold tracking-widest text-white mb-4">CONTACT</h4>
              <ul className="space-y-2 text-[11px] tracking-wide text-white/60">
                <li>+233 542661704</li>
                <li>emefabansa@gmail.com</li>
              </ul>
            </div>
            <div>
              <h4 className="text-[10px] font-bold tracking-widest text-white mb-4">VISIT</h4>
              <ul className="space-y-2 text-[11px] tracking-wide text-white/60">
                <li>Accra, Ghana</li>
                <li>Mon – Sat, 9:00 – 18:00</li>
              </ul>
            </div>
            <div>
              <h4 className="text-[10px] font-bold tracking-widest text-white mb-4">HELP</h4>
              <ul className="space-y-2 text-[11px] tracking-wide text-white/60">
                <li><Link to="/help" className="hover:text-white transition-colors">Customer Support</Link></li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-white/5 w-full text-center">
            <p className="text-[10px] text-white/40 tracking-wider">© {new Date().getFullYear()} EMBA BY EMEFA. ALL RIGHTS RESERVED.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function HomePage() {
  const [heroImage, setHeroImage] = useState(null);
  const [collections, setCollections] = useState(DEFAULT_COLLECTIONS);
  const [banners, setBanners] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
        const res = await fetch(`${baseUrl}/api/homepage/`);

        if (res.ok) {
          const data = await res.json();
          // Handle both direct array and paginated { results: [] } responses
          const items = data.results || data;

          const heroItem = items.find(d => d.section === 'HERO');
          const collectionItems = items.filter(d => d.section === 'COLLECTION');

          if (heroItem && heroItem.image_url) {
            setHeroImage(heroItem.image_url);
          }

          if (collectionItems.length > 0) {
            const formattedCollections = collectionItems.map((item) => ({
              id: item.slug || item.id,
              title: item.title,
              subtitle: item.subtitle,
              img: item.image_url,
              type: item.type || 'portrait',
              path_name: item.path_name,
            }));
            setCollections(formattedCollections);
          }
        }

        // Fetch Banner Image
        const resBanner = await fetch(`${baseUrl}/api/banner-images/`);
        if (resBanner.ok) {
          const bannerData = await resBanner.json();
          const bannerItems = bannerData.results || bannerData;
          if (bannerItems.length > 0) {
            setBanners(bannerItems);
          }
        }
      } catch (e) {
        console.error("Failed to fetch homepage content", e);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, []);

  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
      }, 15000);
      return () => clearInterval(interval);
    }
  }, [banners.length]);

  return (
    <main className="bg-[#EBE6DD] min-h-screen">
      <Header />
      <Hero image={heroImage} banners={banners} currentBannerIndex={currentBannerIndex} isLoading={isLoading} />
      
      <KineticTypography />

      <section className="py-24 md:py-32 w-full">
        <div className="text-center mb-20 px-4 md:px-8">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-[9px] tracking-[0.4em] text-[#13110E]/50 mb-4 uppercase"
          >
            Collections
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-4xl md:text-6xl font-serif text-[#13110E]"
          >
            Curated Excellence
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 w-full">
          {collections.map((item) => (
            <GalleryItem key={item.id} item={item} />
          ))}
        </div>
      </section>

      <section className="bg-[#1b0d0b] py-24 text-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-2xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl font-serif text-white mb-6">Ready to Wear?</h2>
          <p className="text-white/60 mb-10 leading-relaxed font-light">
            Experience the luxury of custom-made attire. Book a consultation with our designers to create something truly unique for your special occasion.
          </p>
          <Link
            to="/bookings"
            className="inline-block border border-white/30 px-8 py-4 text-xs tracking-[0.3em] text-white hover:bg-white hover:text-[#1b0d0b] transition-all duration-300"
          >
            BOOK AN APPOINTMENT
          </Link>
        </motion.div>
      </section>

      {/* EMBA Pocket App Section */}
      <section className="py-24 md:py-32 bg-[#EBE6DD] border-t border-[#13110E]/10 px-4 overflow-hidden">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-16">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left flex-1"
          >
            <h2 className="text-[10px] tracking-[0.4em] text-[#13110E]/50 mb-4 uppercase">Mobile Experience</h2>
            <h3 className="text-4xl md:text-5xl font-serif text-[#13110E] mb-6">EMBA Pocket</h3>
            <p className="text-[#13110E]/70 font-light text-sm md:text-base max-w-lg mx-auto lg:mx-0 leading-relaxed mb-10">
              Experience the luxury of EMBA wherever you go. Download the EMBA Pocket app to easily book appointments, track your orders, and explore new collections right from your phone.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
              <a 
                href="https://apps.apple.com/gh/app/emba-pocket/id6769221737" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center border border-[#13110E] px-8 py-4 w-64 sm:w-auto hover:bg-[#13110E] hover:text-[#EBE6DD] transition-colors duration-300"
              >
                <span className="text-xs tracking-[0.2em] font-medium uppercase">App Store</span>
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=com.emba.embapocket" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center border border-[#13110E] px-8 py-4 w-64 sm:w-auto hover:bg-[#13110E] hover:text-[#EBE6DD] transition-colors duration-300"
              >
                <span className="text-xs tracking-[0.2em] font-medium uppercase">Google Play</span>
              </a>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2, type: "spring", stiffness: 50 }}
            className="relative shrink-0 perspective-1000"
          >
            {/* Minimalist Phone Mockup */}
            <div className="relative w-[280px] h-[580px] bg-[#13110E] rounded-[40px] border-[8px] border-[#13110E] shadow-2xl overflow-hidden shadow-[#13110E]/20 transform lg:-rotate-y-12 lg:rotate-x-6">
              {/* Dynamic Island / Notch */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-[#13110E] rounded-full z-10"></div>
              
              <img 
                src="/assets/images/app-screenshot.png" 
                alt="EMBA Pocket App Interface" 
                className="w-full h-full object-cover object-top rounded-[32px]"
              />
            </div>
            
            {/* Decorative elements behind the phone */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-radial from-[#13110E]/5 to-transparent blur-3xl -z-10 rounded-full"></div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
}